import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { OnboardingWelcome } from "@/components/onboarding/OnboardingWelcome";
import { BaselineQuestions } from "@/components/onboarding/BaselineQuestions";
import { ProfileSetupStep } from "@/components/onboarding/ProfileSetupStep";
import { OnboardingPromptBuilder } from "@/components/onboarding/OnboardingPromptBuilder";
import { OnboardingScoreReveal } from "@/components/onboarding/OnboardingScoreReveal";
import { Loader2 } from "lucide-react";

const ONBOARDING_STORAGE_KEY = "onboarding_progress";
const BUILD_PREFILL_TEXT =
  "Help me write a competitive analysis for the launch of the new LeBron James Nike shoes that are coming out in May. Key competitors are Under Armour and New Balance.";

export interface BaselineData {
  work_ai_platform: string;
  work_ai_platform_other: string;
  ai_weekly_frequency: string;
  ai_comfort_1_10: number;
}

export interface SubmissionResult {
  id: string;
  overall_score: number;
  original_score: number;
  evaluation_markdown: string;
  drift_flag: string | null;
  exercise_type: "paste" | "write" | "build";
  original_content: string;
  submission_type: "prompt_single" | "chat_full";
  dimension_scores_json?: Record<string, { score: number; rationale: string }>;
  points_base?: number;
  points_bonus?: number;
  points_rubric?: number;
  points_total?: number;
  improvements?: string[];
  rewrite_tagged?: string;
  rewrite_clean?: string;
  bubbles?: Array<{
    id: string;
    rubric_driver: string;
    title: string;
    body: string;
    anchor_hint: string;
  }>;
}


export default function Onboarding() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshMembership } = useAuth();

  // Step mapping:
  // 0=profile(conditional), 1=baseline, 2=welcome, 3=build, 4=score-reveal → complete
  const [currentStep, setCurrentStep] = useState(-1);
  const [needsProfileSetup, setNeedsProfileSetup] = useState<boolean | null>(null);
  const [profileData, setProfileData] = useState<{
    full_name?: string;
    first_name?: string;
    last_name?: string;
  } | null>(null);
  const [baselineData, setBaselineData] = useState<BaselineData | null>(null);
  const [buildResult, setBuildResult] = useState<SubmissionResult | null>(null);
  const [buildGoal, setBuildGoal] = useState(BUILD_PREFILL_TEXT);
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoringProgress, setIsRestoringProgress] = useState(true);

  useEffect(() => {
    localStorage.removeItem('cosmo_score_tooltip_seen');
    const saved = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.currentStep !== undefined) {
          setCurrentStep(parsed.currentStep);
          setBaselineData(parsed.baselineData || null);
          setBuildResult(parsed.buildResult || null);
        }
      } catch (e) {
        console.error("Failed to parse onboarding progress from session storage", e);
        sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
      }
    }
    setIsRestoringProgress(false);
  }, []);

  useEffect(() => {
    if (isRestoringProgress) return;
    if (currentStep >= 1) {
      sessionStorage.setItem(
        ONBOARDING_STORAGE_KEY,
        JSON.stringify({
          currentStep,
          baselineData,
          buildResult,
        }),
      );
    }
  }, [currentStep, baselineData, buildResult, isRestoringProgress]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentStep >= 3 && currentStep <= 4) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [currentStep]);

  useEffect(() => {
    const checkProfileStatus = async () => {
      if (!user) return;

      const saved = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (parsed.currentStep >= 1) {
            const { data } = await supabase
              .from("profiles")
              .select("first_name, last_name, full_name, needs_password_setup")
              .eq("user_id", user.id)
              .single();

            if (data) {
              setNeedsProfileSetup(data.needs_password_setup ?? false);
              setProfileData({
                full_name: data.full_name || undefined,
                first_name: data.first_name || undefined,
                last_name: data.last_name || undefined,
              });
            }
            return;
          }
        } catch {
          // Ignore parse errors and continue
        }
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("first_name, last_name, full_name, needs_password_setup")
        .eq("user_id", user.id)
        .single();

      if (error) {
        setNeedsProfileSetup(true);
        setCurrentStep(0);
        return;
      }

      const needsSetup = data?.needs_password_setup === true;
      setNeedsProfileSetup(needsSetup);
      setProfileData({
        full_name: data?.full_name || undefined,
        first_name: data?.first_name || undefined,
        last_name: data?.last_name || undefined,
      });

      setCurrentStep(needsSetup ? 0 : 1);
    };

    if (!authLoading && user) {
      checkProfileStatus();
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  const handleBaselineComplete = async (data: BaselineData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          work_ai_platform: data.work_ai_platform,
          work_ai_platform_other: data.work_ai_platform_other || null,
          ai_weekly_frequency: data.ai_weekly_frequency,
          ai_comfort_1_10: data.ai_comfort_1_10,
        })
        .eq("user_id", user!.id);

      if (error) throw error;

      setBaselineData(data);
      setCurrentStep(2);
    } catch (error) {
      console.error("Error saving baseline:", error);
      toast({
        title: "Error",
        description: "Failed to save your responses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBuildScored = async (result: SubmissionResult) => {
    if (result.exercise_type !== "build") return;
    setBuildResult(result);

    // Navigate to the normal prompt score page
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/compute-fluency-snapshot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
        body: JSON.stringify({ is_onboarding: true }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || "Failed to compute fluency snapshot");
      }
      await refreshMembership();

      // Transform result into the format PromptScorePage expects
      let analysis: Record<string, unknown> = {};
      try {
        analysis = JSON.parse(result.evaluation_markdown);
      } catch {
        // fallback
      }

      const transformedResult = {
        ui: {
          ...analysis,
          score_id: result.id,
          submission_id: result.id,
          is_public: true,
          original_score: result.original_score,
          improved_score: result.overall_score,
          user_id: user!.id,
        },
        telemetry: {
          tags: (analysis as { tags?: string[] }).tags || [],
        },
      };

      sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
      navigate(`/prompt_score/${result.id}`, { state: { result: transformedResult, fromOnboarding: true } });
    } catch (error) {
      console.error("Error finishing onboarding:", error);
      toast({
        title: "Error",
        description: "Failed to finish onboarding. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleProfileSetupComplete = () => {
    setNeedsProfileSetup(false);
    setCurrentStep(1);
  };

  const displayStep = currentStep;
  const showProgressBar = currentStep >= 1 && currentStep <= 4;
  const progressTotalSteps = 4;
  const progressPercent = displayStep >= 4 ? 100 : (displayStep / progressTotalSteps) * 100;

  if (authLoading || currentStep === -1 || isRestoringProgress) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg p-4 md:p-8">
      <div className={`mx-auto space-y-6 transition-all duration-300 ${currentStep >= 4 ? "max-w-5xl" : currentStep === 3 ? "max-w-5xl" : "max-w-2xl"}`}>
        {/* Progress indicator */}
        {showProgressBar && (
          <div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Step {displayStep} of {progressTotalSteps}</span>
                <span>{Math.round(progressPercent)}% complete</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          </div>
        )}

        {currentStep === 0 && needsProfileSetup && user && (
          <ProfileSetupStep
            user={user}
            existingProfile={profileData || undefined}
            onComplete={handleProfileSetupComplete}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        )}

        {currentStep === 1 && (
          <BaselineQuestions onComplete={handleBaselineComplete} isLoading={isLoading} />
        )}

        {currentStep === 2 && (
          <OnboardingWelcome
            defaultGoal={BUILD_PREFILL_TEXT}
            onContinue={(goal) => {
              setBuildGoal(goal);
              setCurrentStep(3);
            }}
          />
        )}

        {currentStep === 3 && (
          <OnboardingPromptBuilder
            initialGoal={buildGoal}
            onScored={handleBuildScored}
            onPhaseChange={() => {}}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            skipResultsView={true}
          />
        )}

      </div>
    </div>
  );
}
